import React, { useEffect, useRef } from "react";
import { X, CheckCircle, Info, AlertTriangle, AlertCircle } from "lucide-react";
import type { ToastDto } from "@stores/notifications/toastStore";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const DEFAULT_DURATION_MS = 5000;

const typeConfig = {
    success: {
        icon: CheckCircle,
        bgColor: "bg-green-500/20 dark:bg-green-500/20",
        borderColor: "border-green-500/50 dark:border-green-500/50",
        iconColor: "text-green-600 dark:text-green-400",
        titleColor: "text-green-900 dark:text-green-100",
        descColor: "text-green-700 dark:text-green-300",
    },
    info: {
        icon: Info,
        bgColor: "bg-blue-500/20 dark:bg-blue-500/20",
        borderColor: "border-blue-500/50 dark:border-blue-500/50",
        iconColor: "text-blue-600 dark:text-blue-400",
        titleColor: "text-blue-900 dark:text-blue-100",
        descColor: "text-blue-700 dark:text-blue-300",
    },
    warning: {
        icon: AlertTriangle,
        bgColor: "bg-amber-500/20 dark:bg-amber-500/20",
        borderColor: "border-amber-500/50 dark:border-amber-500/50",
        iconColor: "text-amber-600 dark:text-amber-400",
        titleColor: "text-amber-900 dark:text-amber-100",
        descColor: "text-amber-700 dark:text-amber-300",
    },
    error: {
        icon: AlertCircle,
        bgColor: "bg-red-500/20 dark:bg-red-500/20",
        borderColor: "border-red-500/50 dark:border-red-500/50",
        iconColor: "text-red-600 dark:text-red-400",
        titleColor: "text-red-900 dark:text-red-100",
        descColor: "text-red-700 dark:text-red-300",
    },
};

export const Toast: React.FC<{ notification: ToastDto; onClose: () => void; duration?: number }> = ({
    notification,
    onClose,
    duration = DEFAULT_DURATION_MS,
}) => {
    const { t } = useTranslation();
    const config = typeConfig[notification.type] ?? typeConfig.info;
    const IconComponent = config.icon;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (duration <= 0) return;
        const timer = setTimeout(() => onCloseRef.current(), duration);
        return () => clearTimeout(timer);
    }, [duration]);

    return (
        <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`relative flex items-start gap-3 p-4 rounded-xl border backdrop-blur-xl ${config.bgColor} ${config.borderColor} shadow-lg min-w-[320px] max-w-[420px]`}
        >
            <div className={`flex-shrink-0 ${config.iconColor}`}>
                <IconComponent size={20} />
            </div>
            <div className="flex-1 min-w-0">
                <h4 className={`font-semibold text-sm ${config.titleColor} mb-1`}>{notification.title}</h4>
                {notification.message && (
                    <p className={`text-xs ${config.descColor} opacity-90`}>{notification.message}</p>
                )}
            </div>
            <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 text-neutral-dark dark:text-neutral-light opacity-40 hover:opacity-70 transition-opacity"
                aria-label={t("components.toast.close")}
            >
                <X size={16} />
            </button>
        </motion.div>
    );
};
